import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { Box, useTheme, IconButton, Typography } from "@mui/material";
import ReactFlow, { Background, Controls, MiniMap, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import WorkflowNode from "./WorkflowNode";
import PlusEdge from "./PlusEdge";
import { useState } from "react";
import ChooseActionDrawer from "./ChooseActionDrawer";
import ChooseTriggerDrawer from "./ChooseTriggerDrawer";
import ChooseInstagramTriggerDrawer from "./ChooseInstagramTriggerDrawer";
import ChooseInstagramActionDrawer from "./ChooseInstagramActionDrawer";
import ChooseFacebookTriggerDrawer from "./ChooseFacebookTriggerDrawer";
import ChooseFacebookActionDrawer from "./ChooseFacebookActionDrawer";
import ChooseWebsiteTriggerDrawer from "./ChooseWebsiteTriggerDrawer";
import ChooseWhatsappWebTriggerDrawer from "./ChooseWhatsappWebTriggerDrawer";
import ChooseWebsiteActionDrawer from "./ChooseWebsiteActionDrawer";
import { useDispatch, useSelector } from "react-redux";
import { getAuthToken } from "../../utils/getAuthToken";
import { removeNodeFromBranch, removeBranch, addNodeToBranch } from "../../redux/Slice/Workflow/workflowSlice";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import LinkIcon from "@mui/icons-material/Link";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import DeleteBranchModal from "./DeleteBranchModal";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

function EndNode({ data }) {
  const leftPosition = data?.isAfterOptionNode ? 15 : 84;
  
  return (
    <Box sx={{
      background: '#b6f7b0',
      color: '#222',
      borderRadius: 2,
      px: 4,
      py: 0.5,
      maxWidth: 50,
      minHeight: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: 16,
      boxShadow: 1,
      border: '1.5px solid #7ed957',
      position: 'relative',
      left: leftPosition
    }}>
      <Handle type="target" position={Position.Top} style={{ background: 'transparent', border: 'none' }} />
      End
    </Box>
  );
}

const getNodes = () => [
  {
    id: "1",
    type: "workflowNode",
    data: {
      heading: "Select a trigger",
      description: "Choose how a contact enters this workflow",
      icon: (
        <CenterFocusStrongIcon sx={{ fontSize: 22, mr: 1, color: "#222" }} />
      ),
      color: "#B2F2F6",
      buttonText: "Select",
    },
    position: { x: 0, y: 20 },
  },
  {
    id: "or-label",
    type: "orLabelNode",
    data: {},
    position: { x: 30, y: 150 },
    draggable: false,
    selectable: false,
  },
  {
    id: "2",
    type: "workflowNode",
    data: {
      heading: "Create a reusable workflow",
      description:
        "Launch from other workflows to reduce duplication, or by teammates from Inbox",
      icon: <LinkIcon sx={{ fontSize: 22, mr: 1, color: "#222" }} />,
      color: "#B2F2F6",
      buttonText: "Set up",
    },
    position: { x: 0, y: 220 },
  },
];

const getEdges = () => [];

const OrLabelNode = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 200,
        mx: "auto",
        my: 2,
        gap: 2,
        background: "transparent",
      }}
    >
      <hr
        style={{
          flex: 1,
          border: 0,
          borderTop: `1.5px solid ${isDark ? "#fff" : "#bbb"}`,
          margin: 0,
        }}
      />
      <Typography
        sx={{
          px: 2,
          fontWeight: 700,
          fontSize: 16,
          color: isDark ? "#fff" : "#222",
          background: "transparent",
        }}
      >
        OR
      </Typography>
      <hr
        style={{
          flex: 1,
          border: 0,
          borderTop: `1.5px solid ${isDark ? "#fff" : "#bbb"}`,
          margin: 0,
        }}
      />
    </Box>
  );
};

// Add CSS for ensuring hovered nodes and connected edges are always on top
// Also add CSS to prevent blurriness from transforms
const hoverNodeStyle = `
  .react-flow__node[style*="z-index: 9999"] {
    z-index: 9999 !important;
  }
  .react-flow__edge[style*="z-index: 9999"] {
    z-index: 9999 !important;
  }
  .react-flow__edge .foreignObject[style*="z-index: 9999"] {
    z-index: 9999 !important;
  }
  .react-flow {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .react-flow__node {
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
`;

// Inject the CSS (only once)
if (typeof document !== 'undefined' && !document.getElementById('react-flow-hover-styles')) {
  const style = document.createElement('style');
  style.id = 'react-flow-hover-styles';
  style.textContent = hoverNodeStyle;
  document.head.appendChild(style);
}

const ActionsTab = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const [openDrawer, setOpenDrawer] = useState(null); // 'trigger', 'action', or null
  const [editTrigger, setEditTrigger] = useState(null);
  const [editAction, setEditAction] = useState(null);
  const [editActionBranch, setEditActionBranch] = useState(null);
  const [editActionNodeId, setEditActionNodeId] = useState(null);
  const [pendingInsert, setPendingInsert] = useState(null); // { branchId, nodeIndex }
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [nodeZIndexes, setNodeZIndexes] = useState({});
  // Measured node sizes reported by React Flow (see onNodesChange).
  const [nodeDimensions, setNodeDimensions] = useState({});

  const { selectedWorkflow } = useSelector((state) => state.workflow);
  const branches = useMemo(
    () => selectedWorkflow?.currentVersion?.branches || { start: [] },
    [selectedWorkflow]
  );

  const workflowId = selectedWorkflow?._id;
  const storageKey = workflowId ? `wf-node-positions-${workflowId}` : null;

  const [nodePositions, setNodePositions] = useState(() => {
    if (!storageKey) return {};
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Which storage key the nodePositions state currently belongs to. On a workflow switch the state
  // still holds the previous workflow's positions until the load effect below runs, and they must
  // not be written under the new workflow's key.
  const positionsOwnerKeyRef = useRef(storageKey);

  // Persist positions to localStorage whenever they change
  useEffect(() => {
    if (!storageKey || positionsOwnerKeyRef.current !== storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(nodePositions));
    } catch {}
  }, [nodePositions, storageKey]);

  // When switching workflows, load that workflow's saved positions (must stay after the persist effect)
  useEffect(() => {
    if (!storageKey) return;
    positionsOwnerKeyRef.current = storageKey;
    setNodeDimensions({});
    try {
      const saved = localStorage.getItem(storageKey);
      setNodePositions(saved ? JSON.parse(saved) : {});
    } catch {
      setNodePositions({});
    }
  }, [storageKey]);

  // Use refs to store handlers for stable nodeTypes/edgeTypes
  const handlersRef = useRef({
    handleDeleteNode: null,
    handleNodeHover: null,
    handlePlusClick: null,
  });
  const hoveredNodeIdRef = useRef(null);

  const handlePlusClick = useCallback((branchId, nodeIndex) => {
    console.log('=== handlePlusClick ===');
    console.log('branchId:', branchId);
    console.log('nodeIndex:', nodeIndex);
    console.log('Current branches:', branches);
    console.log('Branch being modified:', branches[branchId]);
    
    setPendingInsert({ branchId, nodeIndex });
    setOpenDrawer('action');
  }, [branches]);

  const performDelete = useCallback(async ({ branchId, nodeId, deleteOption = 'delete-all', selectedBranch = null }) => {
    try {
      const currentBranches = branches || {};
      const branch = currentBranches[branchId] || [];
      const node = branch.find((n) => n.id === nodeId || n._id === nodeId);

      let branchesToRemove = [];
      let branchToPreserve = null;
      
      if (node && node.type === 'action' && node.action && node.action.name === 'Send list message') {
        const nextIds = Array.isArray(node.next) ? node.next : [];
        branchesToRemove = nextIds.filter((id) => typeof id === 'string' && (id.startsWith('option-') || id.startsWith('option-')));
        
        // If save-specific option is selected, handle branch preservation
        if (deleteOption === 'save-specific' && selectedBranch) {
          branchToPreserve = selectedBranch;
          branchesToRemove = branchesToRemove.filter(bid => bid !== selectedBranch);
        }
      } else if (node && node.type === 'action' && node.action && node.action.name === 'AI Reply') {
        const nextIds = Array.isArray(node.next) ? node.next : [];
        branchesToRemove = nextIds.filter(id => typeof id === 'string' && id.startsWith('ai-action-'));
      } else if (node && node.type === 'action' && node.action && node.action.name === 'Send image message') {
        // For image message actions, no special branch handling needed
        branchesToRemove = [];
      } else if (node && node.type === 'action' && Array.isArray(node.next)) {
        // Keyword branches (QR workflows) hang off a plain message action
        branchesToRemove = node.next.filter((id) => typeof id === 'string' && id.startsWith('keyword-'));
      }

      // Remove the node itself
    dispatch(removeNodeFromBranch({ branchId, nodeId }));

      // Handle branch preservation logic
      if (branchToPreserve && currentBranches[branchToPreserve]) {
        const preservedBranch = currentBranches[branchToPreserve];
        
        // Remove the first object (trigger) from the preserved branch
        if (preservedBranch.length > 1) {
          const remainingObjects = preservedBranch.slice(1); // Remove first object (index 0)
          
          // Add remaining objects to the parent branch
          remainingObjects.forEach((obj, index) => {
            // Generate a new unique ID for the moved object
            const newId = `${Date.now()}-${index}`;
            const movedObject = { ...obj, id: newId };
            
            dispatch(addNodeToBranch({
              branchId,
              node: movedObject,
              nodeIndex: undefined // Add to end of parent branch
            }));
          });
        }
        
        // Remove the preserved branch after moving its contents
        dispatch(removeBranch({ branchId: branchToPreserve }));
      }

      // Remove other option/no-reply branches
      if (branchesToRemove.length > 0) {
        branchesToRemove.forEach((bid) => dispatch(removeBranch({ branchId: bid })));
      }

      // Persist updated workflow to backend
    setTimeout(async () => {
      const state = window.store?.getState ? window.store.getState() : null;
      const updatedWorkflow = state?.workflow?.selectedWorkflow;
      if (updatedWorkflow && updatedWorkflow._id) {
        // Get token from memory (not localStorage)
        const token = await getAuthToken();
        if (!token) {
          console.error("No token found");
          return;
        }
        try {
          await axios.put(
            `${SERVER_DOMAIN}/api/workflow/update/${updatedWorkflow._id}`,
            updatedWorkflow,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } catch (err) {
          console.error('Failed to update workflow after node delete', err);
        }
      }
    }, 0);
    } catch (error) {
      console.error('Error performing delete', error);
    }
  }, [branches, dispatch]);

  const handleDeleteNode = useCallback(async ({ branchId, nodeId }) => {
    try {
      // Check if it's a list message action
      const currentBranches = branches || {};
      const branch = currentBranches[branchId] || [];
      const node = branch.find((n) => n.id === nodeId || n._id === nodeId);

      if (node && node.type === 'action' && node.action && node.action.name === 'Send list message') {
        // Show modal for list message actions
        setNodeToDelete({ branchId, nodeId, node });
        setShowDeleteModal(true);
      } else if (node && node.type === 'action' && node.action && node.action.name === 'Send image message') {
        // For image message actions, delete directly (no special handling needed)
        await performDelete({ branchId, nodeId });
      } else {
        // For other actions, delete directly
        await performDelete({ branchId, nodeId });
      }
    } catch (error) {
      console.error('Error handling node delete', error);
    }
  }, [branches, performDelete]);


  const handleDeleteModalConfirm = useCallback(async (modalData) => {
    if (nodeToDelete) {
      await performDelete({
        branchId: nodeToDelete.branchId,
        nodeId: nodeToDelete.nodeId,
        deleteOption: modalData.deleteOption,
        selectedBranch: modalData.selectedBranch
      });
    }
    setShowDeleteModal(false);
    setNodeToDelete(null);
  }, [nodeToDelete, performDelete]);

  // Function to handle node hover with dynamic z-index management
  const handleNodeHover = useCallback((nodeId) => {
    setHoveredNodeId(nodeId);
    
    if (nodeId) {
      // Set the hovered node to the highest z-index
      setNodeZIndexes(prev => ({
        ...prev,
        [nodeId]: 9999
      }));
    } else {
      // Reset all z-indexes when no node is hovered
      setNodeZIndexes({});
    }
  }, []);

  const onNodesChange = useCallback((changes) => {
    // The nodes prop is controlled and rebuilt on every hover (z-index) or drag. React Flow rebuilds
    // its internal nodes from that prop, so measured width/height must be fed back in or the nodes
    // are left without dimensions and stay visibility:hidden (they "disappear") until re-measured.
    setNodeDimensions(prev => {
      let updated = null;
      changes.forEach(c => {
        if (c.type !== 'dimensions' || !c.dimensions) return;
        const { width, height } = c.dimensions;
        if (prev[c.id]?.width === width && prev[c.id]?.height === height) return;
        updated = updated || { ...prev };
        updated[c.id] = { width, height };
      });
      return updated || prev;
    });

    setNodePositions(prev => {
      const updated = { ...prev };
      let changed = false;
      changes.forEach(c => {
        if (c.type === 'position' && c.position) {
          updated[c.id] = c.position;
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, []);

  // Update refs when handlers change
  useEffect(() => {
    handlersRef.current.handleDeleteNode = handleDeleteNode;
    handlersRef.current.handleNodeHover = handleNodeHover;
    handlersRef.current.handlePlusClick = handlePlusClick;
    hoveredNodeIdRef.current = hoveredNodeId;
  }, [handleDeleteNode, handleNodeHover, handlePlusClick, hoveredNodeId]);

  function PlusNode({ data, hoveredNodeId }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    
    // Check if this plus node should be elevated (if its parent node is hovered)
    const isParentHovered = hoveredNodeId && (
      hoveredNodeId === data.parentNodeId || 
      hoveredNodeId === `${data.branchId}-${data.nodeIndex}` ||
      hoveredNodeId === `${data.branchId}-${data.nodeIndex - 1}`
    );
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: 48, 
        width: 48, 
        position: 'relative',
        zIndex: isParentHovered ? 10000 : 10000, // Very high z-index to always appear above nodes
        transform: isParentHovered ? 'scale(1.1)' : 'scale(1)',
        transition: 'all 0.2s ease-in-out',
        pointerEvents: 'all',
      }}>
        {/* Top handle for edge connection */}
        <Handle type="target" position={Position.Top} style={{ background: 'transparent', border: 'none' }} />
        <IconButton
          size="large"
          onClick={() => handlersRef.current.handlePlusClick?.(data.branchId, data.nodeIndex)}
          sx={{
            bgcolor: isDark ? '#23272b' : '#fff',
            border: `2px solid ${isDark ? '#444' : '#b0b0b0'}`,
            boxShadow: isParentHovered ? 4 : 2,
            p: 1,
            '&:hover': { bgcolor: isDark ? '#2e4d44' : '#e0f7fa' },
            color: isDark ? theme.palette.primary.light : theme.palette.primary.main,
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <AddIcon fontSize="large" />
        </IconButton>
      </Box>
    );
  }

  // Horizontal branch node that represents the splitting line
  function HorizontalBranchNode({ data }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { branchCount = 0, startX = 0, endX = 0, y = 0 } = data;
    
    if (branchCount === 0) return null;
    
    const width = endX - startX;
    const height = 4; // Height of the horizontal line
    
    return (
      <Box
        sx={{
          position: 'absolute',
          left: startX,
          top: y,
          width: width,
          height: height,
          bgcolor: isDark ? '#666' : '#999',
          borderRadius: 2,
          zIndex: 1,
        }}
      >
        {/* Vertical lines dropping from the horizontal line to each branch */}
        {Array.from({ length: branchCount }, (_, i) => {
          const x = (width / (branchCount - 1)) * i;
          return (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: x,
                top: height,
                width: 2,
                height: 50, // Height of vertical drop
                bgcolor: isDark ? '#666' : '#999',
                borderRadius: 1,
              }}
            />
          );
        })}
      </Box>
    );
  }

  // Custom edge component for "go to action" connections
  function GoToActionEdge({ id, sourceX, sourceY, targetX, targetY, style = {}, markerEnd, data }) {
    const isKeywordEdge = !!data?.isKeywordTarget;
    const baseStyle = {
      ...style,
      strokeWidth: style?.strokeWidth ?? 2,
      strokeDasharray: style?.strokeDasharray ?? '8,4',
      fill: 'none',
    };
    baseStyle.stroke = isKeywordEdge ? '#4caf50' : '#ff6b6b';
    
    // Adjust source and target positions for better curves
    // Source: bottom of the source node (add 20px to sourceY)
    // Target: top of the target node (subtract 20px from targetY)
    const adjustedSourceY = sourceY + 20;
    const adjustedTargetY = targetY - 20;
    
    // Create a more pronounced curved path for go-to-action edges
    const dx = targetX - sourceX;
    const dy = adjustedTargetY - adjustedSourceY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Calculate control points for a more pronounced curve
    const curveIntensity = Math.min(distance * 0.4, 120); // Increased curve intensity
    
    // For horizontal connections, curve vertically
    if (Math.abs(dx) > Math.abs(dy)) {
      const midX = sourceX + dx * 0.5;
      const controlY1 = adjustedSourceY + curveIntensity;
      const controlY2 = adjustedTargetY + curveIntensity;
      
      const edgePath = `M ${sourceX} ${adjustedSourceY} C ${midX} ${controlY1} ${midX} ${controlY2} ${targetX} ${adjustedTargetY}`;
      
      return (
        <g>
          <path
            id={id}
            style={baseStyle}
            className="react-flow__edge-path"
            d={edgePath}
            markerEnd={markerEnd}
          />
        </g>
      );
    } else {
      // For vertical connections, curve horizontally
      const midY = adjustedSourceY + dy * 0.5;
      const controlX1 = sourceX + curveIntensity;
      const controlX2 = targetX + curveIntensity;
      
      const edgePath = `M ${sourceX} ${adjustedSourceY} C ${controlX1} ${midY} ${controlX2} ${midY} ${targetX} ${adjustedTargetY}`;
      
      return (
        <g>
          <path
            id={id}
            style={baseStyle}
            className="react-flow__edge-path"
            d={edgePath}
            markerEnd={markerEnd}
          />
        </g>
      );
    }
  }

  // Create stable nodeTypes - define once with stable wrapper functions
  const nodeTypes = useRef({
    workflowNode: (props) => (
      <WorkflowNode 
        {...props} 
        onDelete={handlersRef.current.handleDeleteNode} 
        onHover={handlersRef.current.handleNodeHover} 
      />
    ),
    orLabelNode: OrLabelNode,
    end: EndNode,
    plus: (props) => (
      <PlusNode {...props} hoveredNodeId={hoveredNodeIdRef.current} />
    ),
    horizontalBranch: HorizontalBranchNode,
  }).current;

  // Create stable edgeTypes - define once with stable wrapper functions
  const edgeTypes = useRef({
    plus: (edgeProps) => (
      <PlusEdge
        {...edgeProps}
        onPlusClick={() =>
          handlersRef.current.handlePlusClick?.(edgeProps.data?.branchId, edgeProps.data?.nodeIndex)
        }
      />
    ),
    goToAction: GoToActionEdge,
  }).current;

  // Memoize nodes and edges generation to prevent disappearing on re-renders
  const { nodes: generatedNodes, edges: generatedEdges } = useMemo(() => {
    // Helper function to estimate node height based on content
    // "Send Template message" action nodes render field.type === 'template' via
    // <TemplateSendPreview> (WorkflowNode.jsx) — a card showing the template name, header,
    // body text, footer, and buttons, same layout as TemplateSendFeatures/TemplateSendPreview.jsx.
    // estimateNodeHeight below used to budget a flat 25px for this field regardless of the
    // selected template's actual content, so a template with a long body would render a much
    // taller card than estimated — pushing the next node's "+" button (positioned by this
    // estimate, not measured DOM height) up under the bottom of the card instead of below it.
    // Mirrors that component's per-component layout closely enough to keep the two in sync
    // without measuring real DOM height.
    function estimateTemplatePreviewHeight(template) {
      const components = Array.isArray(template?.components) ? template.components : [];
      const headerComp = components.find(c => (c.type || '').toUpperCase() === 'HEADER');
      const bodyComp = components.find(c => (c.type || '').toUpperCase() === 'BODY');
      const footerComp = components.find(c => (c.type || '').toUpperCase() === 'FOOTER');
      const buttonsComp = components.find(c => (c.type || '').toUpperCase() === 'BUTTONS');
      const carouselComp = components.find(c => (c.type || '').toLowerCase() === 'carousel');

      // Narrower than the 45-chars-per-line estimate below: the preview card caps at ~250px wide.
      const charsPerLine = 32;
      const estimatedLines = (text) => {
        if (!text) return 0;
        const str = String(text);
        const lineBreaks = (str.match(/\n/g) || []).length;
        return Math.max(Math.ceil(str.length / charsPerLine), lineBreaks + 1);
      };

      let height = 40; // template-name header bar
      if (headerComp) {
        height += (headerComp.format || '').toUpperCase() === 'TEXT'
          ? estimatedLines(headerComp.text) * 22 + 15
          : 100; // media (image/video/document) header placeholder block
      }
      if (bodyComp) height += estimatedLines(bodyComp.text) * 20 + 10;
      if (footerComp) height += estimatedLines(footerComp.text) * 16 + 12;
      if (carouselComp) height += 50;
      if (buttonsComp && Array.isArray(buttonsComp.buttons)) height += buttonsComp.buttons.length * 34 + 16;
      return height;
    }

    function estimateNodeHeight(node, isBeforeEndNode = false) {
      const baseHeight = 80; // Base height for header and padding
      let contentHeight = 0;

      if (node.type === 'action' && node.action?.config) {
        const config = node.action.config;
        if (Array.isArray(config)) {
          config.forEach(section => {
            if (Array.isArray(section.fields)) {
              section.fields.forEach(field => {
                if (field.selectedValue) {
                  const value = String(field.selectedValue);
                  // Estimate height based on text length (assuming ~50 chars per line, ~20px per line)
                  if (field.type === 'text large' || field.type === 'text') {
                    // For text large, account for word wrapping and line breaks
                    // Use a more conservative estimate (fewer chars per line for better accuracy)
                    const charsPerLine = 45; // Reduced from 50 for more accurate estimation
                    const lineBreaks = (value.match(/\n/g) || []).length;
                    const estimatedLines = Math.max(
                      Math.ceil(value.length / charsPerLine), // Character-based estimate
                      lineBreaks + 1 // At least one line per line break
                    );
                    contentHeight += estimatedLines * 22 + 25; // Increased line height and padding
                  } else if (field.type === 'heading') {
                    contentHeight += 30;
                  } else if (field.type === 'template' && field.selectedValue && Array.isArray(field.selectedValue.components) && field.selectedValue.components.length > 0) {
                    contentHeight += estimateTemplatePreviewHeight(field.selectedValue);
                  } else {
                    contentHeight += 25; // Default for other field types
                  }
                }
              });
            }
          });
        }
      }

      // Add minimum spacing buffer and extra padding for bottom spacing
      // Add extra padding if this node is before END node
      const bottomPadding = isBeforeEndNode ? 40 : 20;
      return Math.max(baseHeight + contentHeight + bottomPadding, 140);
    }

    // Recursively build nodes and edges from branches
    let nodes = [];
    let edges = [];
    const centerX = 400;
    const branchYSpacing = 100;
    const baseOptionXSpacing = 400; // Increased base spacing for upper levels
    const minOptionXSpacing = 150; // Decreased minimum spacing for options levels
    const levelSpacingMultiplier = 0.8; // Decrease spacing by 20% for each level deeper
    let branchPositions = { start: { x: centerX, y: 20 } };

  // Count the number of leaf branches in a subtree — each leaf gets one horizontal slot.
  // When a branch adds children its leaf count grows, which automatically widens the
  // parent-level spacing so siblings never overlap.
  function calculateSubtreeLeaves(branchId, visited = new Set()) {
    if (visited.has(branchId)) return 1;
    visited.add(branchId);

    const branch = branches[branchId] || [];
    let maxLeaves = 0;
    let hasBranching = false;

    for (const node of branch) {
      if (Array.isArray(node.next) && node.next.length > 0) {
        hasBranching = true;
        const childLeaves = node.next.reduce((sum, nextBranchId) => {
          return sum + calculateSubtreeLeaves(nextBranchId, new Set(visited));
        }, 0);
        maxLeaves = Math.max(maxLeaves, childLeaves);
      }
    }

    return hasBranching ? maxLeaves : 1;
  }

  // Helper function to find "go to action" configuration in action config
  function findGoToActionConfig(config) {
    if (!Array.isArray(config)) return null;
    
    for (const section of config) {
      if (section.fields && Array.isArray(section.fields)) {
        for (const field of section.fields) {
          if (field.type === 'actions' && field.selectedValue) {
            // Check if selectedValue is an object with branchId and nodeIndex
            if (typeof field.selectedValue === 'object' && 
                field.selectedValue.branchId && 
                field.selectedValue.nodeIndex !== undefined) {
              return field.selectedValue;
            }
          }
        }
      }
    }
    return null;
  }

  // Media template: get expiry state from Media URL field (uploaded_at) for canvas badge
  function getMediaExpiryFromConfig(config) {
    if (!config || !Array.isArray(config)) return { mediaExpiry: null, mediaExpiryDays: null };
    for (const section of config) {
      const fields = section.fields;
      if (!Array.isArray(fields)) continue;
      for (const field of fields) {
        if (field.label && field.label.includes("Media URL") && field.type === "file") {
          const val = field.selectedValue;
          const uploadedAt = val && typeof val === "object" ? val.uploaded_at : null;
          if (!uploadedAt) return { mediaExpiry: null, mediaExpiryDays: null };
          const up = new Date(uploadedAt).getTime();
          const expiresAt = up + 90 * 24 * 60 * 60 * 1000;
          const now = Date.now();
          if (now > expiresAt) return { mediaExpiry: "expired", mediaExpiryDays: 0 };
          const daysLeft = Math.ceil((expiresAt - now) / (24 * 60 * 60 * 1000));
          if (daysLeft <= 7) return { mediaExpiry: "expiring_soon", mediaExpiryDays: daysLeft };
          return { mediaExpiry: "ok", mediaExpiryDays: daysLeft };
        }
      }
    }
    return { mediaExpiry: null, mediaExpiryDays: null };
  }

  // Helper function to find keyword targets in action
  function findKeywordTargets(action) {
    if (!action || !action.keywords || !Array.isArray(action.keywords)) {
      return [];
    }
    
    // Return array of keyword target IDs
    return action.keywords
      .filter(kw => kw.targetId)
      .map(kw => kw.targetId);
  }

  // Helper function to find the target node ID based on branchId and nodeIndex
  function findTargetNodeId(targetBranchId, targetNodeIndex) {
    const targetBranch = branches[targetBranchId];
    if (!targetBranch || !Array.isArray(targetBranch)) return null;
    
    const targetNode = targetBranch[targetNodeIndex];
    if (!targetNode) return null;
    
    return `${targetBranchId}-${targetNode.id || targetNodeIndex}`;
  }

  function traverseBranch(branchId, x, y, parentNodeId = null, isAfterOptionNode = false, level = 0) {
    const branch = branches[branchId] || [];
    let lastNodeId = null;
    let lastNodeHasNext = false;
    let lastNodeWasOption = false;
    let lastNodeHasGoToAction = false; // Track if the last node has a go-to-action
    let currentY = y;
    
    branch.forEach((node, idx) => {
      const nodeId = `${branchId}-${node.id || idx}`;
      let icon, color, heading, description;
      
      // Check if this is an option node (only the first node in an option branch, button branch, no-reply branch, or ai-action branch)
      const isOptionNode = (branchId.startsWith('option-') || branchId.startsWith('button-') || branchId.startsWith('keyword-') || branchId.startsWith('no-reply-') || branchId.startsWith('ai-action-')) && idx === 0;
      
      // Check if the previous node in this branch was an option node
      const isAfterOptionNodeInBranch = idx > 0 && lastNodeWasOption;
      
      // Helper function to extract button text from trigger config
      const extractButtonText = (trigger) => {
        if (!trigger || !trigger.config || !Array.isArray(trigger.config)) return null;
        for (const section of trigger.config) {
          if (section.fields && Array.isArray(section.fields)) {
            for (const field of section.fields) {
              // Check for Button Title field (can be type 'text' or 'variable')
              if ((field.label === 'Button Title' || field.label === 'Title' || field.label === 'Keyword') && field.selectedValue) {
                return field.selectedValue;
              }
            }
          }
        }
        return null;
      };
      
      if (node.type === 'trigger') {
        icon = <CenterFocusStrongIcon sx={{ fontSize: 22, mr: 1, color: "#222" }} />;
        color = "#B2F2F6";
        heading = node.trigger?.name || "Trigger";
        description = node.trigger?.description || "";
      } else if (isOptionNode) {
        // Style option nodes as small, compact cards
        icon = <CenterFocusStrongIcon sx={{ fontSize: 22, mr: 1, color: "#222" }} />;
        color = "#E8E8E8"; // Light gray background
        
        // For button reply triggers, extract button text from config
        if ((branchId.startsWith('button-') || branchId.startsWith('keyword-')) && node.trigger) {
          const buttonText = extractButtonText(node.trigger);
          heading = buttonText || node.trigger?.name || (branchId.startsWith('keyword-') ? "Keyword" : "Button");
        } else if (branchId.startsWith('ai-action-') && node.trigger) {
          heading = node.trigger?.name || "AI Action";
        } else {
          heading = node.trigger?.name || "Option";
        }
        description = node.trigger?.description || ""; // Use trigger description
      } else {
        icon = <ChatBubbleOutlineIcon sx={{ color: "#13795B", mr: 1 }} />;
        color = "#B2F2D6";
        heading = node.action?.name || "Action";
        description = node.action?.description || "";
      }
      
      // Calculate dynamic spacing based on previous node's height
      if (idx > 0) {
        const prevNode = branch[idx - 1];
        const prevNodeHeight = estimateNodeHeight(prevNode);
        currentY += prevNodeHeight + 40; // spacing buffer between nodes
      }
      
      // Check if this is the last node in the branch and will have an END node after it
      const isLastNodeInBranch = idx === branch.length - 1;
      const willHaveEndNode = isLastNodeInBranch && 
        (!node.next || !Array.isArray(node.next) || node.next.length === 0) &&
        !findGoToActionConfig(node.action?.config || node.trigger?.config);
      
      const nodeData = {
        heading,
        description,
        icon,
        color,
        type: node.type,
        config: node.trigger?.config || node.action?.config,
        stepRaw: node,
        branchId,
        nodeId: node.id || idx,
        isOptionNode, // Add flag to identify option nodes
        isAfterOptionNode: isAfterOptionNodeInBranch, // Check if previous node in this branch was an option
        isBeforeEndNode: willHaveEndNode, // Flag to indicate this node is before END node
      };
      if (node.type === "action" && node.action?.name === "Send media template message") {
        const expiry = getMediaExpiryFromConfig(node.action.config);
        nodeData.mediaExpiry = expiry.mediaExpiry;
        nodeData.mediaExpiryDays = expiry.mediaExpiryDays;
      }
      nodes.push({
        id: nodeId,
        type: "workflowNode",
        data: nodeData,
        position: { x, y: currentY },
      });
      
      // Update currentY for next node based on this node's height
      const currentNodeHeight = estimateNodeHeight(node, willHaveEndNode);
      currentY += currentNodeHeight; // Move to bottom of current node
      
      if (lastNodeId && !lastNodeHasGoToAction) {
        edges.push({
          id: `${lastNodeId}-to-${nodeId}`,
          source: lastNodeId,
          target: nodeId,
          type: "plus",
          data: { branchId, nodeIndex: idx },
        });
      }
      
      lastNodeId = nodeId;
      lastNodeWasOption = isOptionNode;
      
      // Check for "go to action" configuration in action nodes
      let currentNodeHasGoToAction = false;
      if (node.type === 'action' && node.action && node.action.config) {
        const goToActionConfig = findGoToActionConfig(node.action.config);
        if (goToActionConfig) {
          currentNodeHasGoToAction = true;
          console.log('=== Found Go To Action Configuration ===');
          console.log('Source node:', nodeId);
          console.log('Go to action config:', goToActionConfig);
          
          // Find the target node based on branchId and nodeIndex
          const targetNodeId = findTargetNodeId(goToActionConfig.branchId, goToActionConfig.nodeIndex);
          if (targetNodeId) {
            console.log('Creating go-to-action edge from', nodeId, 'to', targetNodeId);
            
            // Add a special edge for "go to action"
            edges.push({
              id: `${nodeId}-goto-${targetNodeId}`,
              source: nodeId,
              target: targetNodeId,
              type: "goToAction", // Special edge type for go-to-action
              data: { 
                branchId, 
                nodeIndex: idx,
                isGoToAction: true,
                targetBranchId: goToActionConfig.branchId,
                targetNodeIndex: goToActionConfig.nodeIndex
              },
              animated: true,
              style: { stroke: '#ff6b6b', strokeWidth: 2, strokeDasharray: '5,5' }
              // Make it animated to distinguish from normal edges
            });
          }
        }
      }
      
      // Check for keyword targets in action nodes
      if (node.type === 'action' && node.action) {
        const keywordTargetIds = findKeywordTargets(node.action);
        if (keywordTargetIds.length > 0) {
          console.log('=== Found Keyword Targets ===');
          console.log('Source node:', nodeId);
          console.log('Keyword target IDs:', keywordTargetIds);
          
          // Find target nodes for each keyword
          for (const targetMessageId of keywordTargetIds) {
            // Search all branches to find the node with this ID
            let targetNodeId = null;
            let targetBranchId = null;
            let targetNodeIndex = null;
            
            for (const [searchBranchId, searchBranch] of Object.entries(branches)) {
              if (!Array.isArray(searchBranch)) continue;
              const foundIndex = searchBranch.findIndex(n => n.id === targetMessageId || n._id === targetMessageId);
              if (foundIndex !== -1) {
                targetBranchId = searchBranchId;
                targetNodeIndex = foundIndex;
                targetNodeId = findTargetNodeId(searchBranchId, foundIndex);
                break;
              }
            }
            
            if (targetNodeId) {
              console.log('Creating keyword target edge from', nodeId, 'to', targetNodeId);
              
              // Add a special edge for keyword target (different color/style from go-to-action)
              edges.push({
                id: `${nodeId}-keyword-${targetMessageId}`,
                source: nodeId,
                target: targetNodeId,
                type: "goToAction", // Reuse the same edge type for visual consistency
                data: { 
                  branchId, 
                  nodeIndex: idx,
                  isKeywordTarget: true,
                  targetMessageId: targetMessageId,
                  targetBranchId: targetBranchId,
                  targetNodeIndex: targetNodeIndex
                },
                animated: true,
                style: { stroke: '#4caf50', strokeWidth: 2, strokeDasharray: '5,5' } // Green dashed line for keywords
              });
            }
          }
        }
      }
      
      // Update tracking variables
      lastNodeHasGoToAction = currentNodeHasGoToAction;
      
      // Check if this node has next branches (horizontal branching)
      if (Array.isArray(node.next) && node.next.length > 0) {
        lastNodeHasNext = true;
        console.log(`Node ${nodeId} has next branches:`, node.next);
        
        // Leaf-weight layout: each branch gets horizontal space proportional to the
        // number of leaves in its subtree. Adding sub-branches automatically pushes
        // siblings further apart on the parent level, preventing overlaps.
        const leafWeights = node.next.map(nextBranchId => calculateSubtreeLeaves(nextBranchId));
        const totalLeaves = leafWeights.reduce((sum, w) => sum + w, 0);
        const leafSpacing = Math.max(
          baseOptionXSpacing * Math.pow(levelSpacingMultiplier, level),
          minOptionXSpacing
        );
        const totalWidth = (totalLeaves - 1) * leafSpacing;
        const startX = x - totalWidth / 2;
        // Pre-compute each branch's center X based on its leaf-weight slot
        let leafOffset = 0;
        const branchXPositions = node.next.map((_, bIdx) => {
          const w = leafWeights[bIdx];
          const branchX = startX + leafOffset + ((w - 1) / 2) * leafSpacing;
          leafOffset += w * leafSpacing;
          return branchX;
        });
        // Calculate branch Y position based on current node position and estimated height
        const currentNodeHeight = estimateNodeHeight(node);
        const branchY = currentY + currentNodeHeight + branchYSpacing;
        
        // Create edges from this node to each option node with horizontal routing
        node.next.forEach((nextBranchId, bIdx) => {
          const nextBranch = branches[nextBranchId] || [];
          if (nextBranch.length > 0) {
            const nextNodeId = `${nextBranchId}-${nextBranch[0].id || 0}`;
            console.log(`Creating edge from ${nodeId} to ${nextNodeId} (first node of branch ${nextBranchId})`);
            
            edges.push({
              id: `${nodeId}-to-${nextNodeId}`,
              source: nodeId,
              target: nextNodeId,
              type: "plus",
              data: { 
                branchId: nextBranchId, 
                nodeIndex: 0,
                isHorizontalBranch: true, // Flag for horizontal branching
                branchIndex: bIdx,
                totalBranches: node.next.length
              },
            });
          }
          
          // Each branch is centered inside its leaf-weight slot
          const branchX = branchXPositions[bIdx];
          branchPositions[nextBranchId] = { x: branchX, y: branchY };
          
          // Recursively traverse the next branch with incremented level
          traverseBranch(nextBranchId, branchX, branchY, nodeId, false, level + 1); // Pass incremented level
        });
      }
    });
    
    // Only add End node if the last node doesn't have any next branches AND doesn't have a go-to-action
    if (branch.length > 0 && !lastNodeHasNext && !lastNodeHasGoToAction) {
      const lastNodeId = `${branchId}-${branch[branch.length - 1].id || (branch.length - 1)}`;
      const endNodeId = `${lastNodeId}-endnode`;
      
      // Get the last node to check if it has long text content
      const lastNode = branch[branch.length - 1];
 // true because it's before END node
      
      // Calculate additional spacing based on content - if node has text large, add more space
      let additionalSpacing = 0;
      if (lastNode.type === 'action' && lastNode.action?.config) {
        const config = lastNode.action.config;
        if (Array.isArray(config)) {
          config.forEach(section => {
            if (Array.isArray(section.fields)) {
              section.fields.forEach(field => {
                if (field.type === 'text large' && field.selectedValue) {
                  const value = String(field.selectedValue);
                  const lineBreaks = (value.match(/\n/g) || []).length;
                  const estimatedLines = Math.max(
                    Math.ceil(value.length / 50),
                    lineBreaks + 1
                  );
                  // Add extra spacing for very long text (more than 5 lines)
                  if (estimatedLines > 5) {
                    additionalSpacing += (estimatedLines - 5) * 15;
                  }
                }
              });
            }
          });
        }
      }
      
      // currentY is already at the bottom of the last node after the forEach loop
      // Add larger spacing buffer to position the end node below, accounting for long text
      const endNodeY = currentY + 80 + additionalSpacing;
      
      // Add an EndNode below the last node
      nodes.push({
        id: endNodeId,
        type: 'end',
        data: { isAfterOptionNode: lastNodeWasOption }, // Pass whether the last node was an option
        position: { x, y: endNodeY },
        selectable: false,
      });
      
      edges.push({
        id: `${lastNodeId}-to-end`,
        source: lastNodeId,
        target: endNodeId,
        type: "plus", // Use PlusEdge so the plus button appears on this edge
        data: { branchId, nodeIndex: branch.length },
      });
    }
  }
    // Start traversal from 'start' branch with level 0
    traverseBranch('start', centerX, 20, null, false, 0);

    return { nodes, edges };
  }, [branches]); // Only recalculate when branches change

  // Add hover highlighting to edges and dynamic z-index to nodes
  const highlightedEdges = useMemo(() => generatedEdges.map(edge => {
    const isConnectedHovered = hoveredNodeId && (edge.source === hoveredNodeId || edge.target === hoveredNodeId);
    
    return {
      ...edge,
      data: {
        ...edge.data,
        isConnectedHovered,
      },
      style: {
        ...edge.style,
        strokeWidth: isConnectedHovered ? 3 : 1,
        stroke: isConnectedHovered ? '#ff6b6b' : '#b1b1b7',
        transition: 'all 0.2s ease-in-out',
        zIndex: isConnectedHovered ? 9999 : 'auto',
      }
    };
  }), [generatedEdges, hoveredNodeId]);

  // Update nodes with dynamic z-index and user-dragged positions
  const nodesWithZIndex = useMemo(() => generatedNodes.map(node => ({
    ...node,
    position: nodePositions[node.id] || node.position,
    ...nodeDimensions[node.id],
    draggable: node.draggable !== false,
    style: {
      ...node.style,
      zIndex: nodeZIndexes[node.id] || 'auto',
    }
  })), [generatedNodes, nodeZIndexes, nodePositions, nodeDimensions]);

  const onNodeClick = (event, node) => {
    console.log('=== onNodeClick ===');
    console.log('Node data:', node.data);
    
    const { branchId, nodeId, type, stepRaw, buttonText, isOptionNode } = node.data || {};
    console.log('Extracted data:', { branchId, nodeId, type, buttonText, isOptionNode });
    
    // Check if we're in keyword target selection mode
    if (window.isKeywordTargetSelectionMode && typeof window.isKeywordTargetSelectionMode === 'function' && window.isKeywordTargetSelectionMode()) {
      console.log('Keyword target selection mode detected, checking if this is an action node');
      
      // In keyword target selection mode, only allow clicking on action nodes
      if (type === 'action' && stepRaw && stepRaw.action) {
        console.log('Action node clicked in keyword target selection mode');
        
        // Find the node index in the branch
        const branch = branches[branchId] || [];
        const nodeIndex = branch.findIndex(n => 
          n.id === nodeId || 
          n._id === nodeId || 
          n.id === parseInt(nodeId) || 
          n._id === parseInt(nodeId)
        );
        
        if (nodeIndex !== -1) {
          const actionData = {
            branchId,
            nodeIndex,
            action: stepRaw.action
          };
          
          console.log('Calling keyword target selection handler with:', actionData);
          if (window.handleKeywordTargetSelection && typeof window.handleKeywordTargetSelection === 'function') {
            window.handleKeywordTargetSelection(actionData);
          }
          
          // Add visual feedback
          event.target.style.transform = 'scale(1.05)';
          event.target.style.transition = 'transform 0.2s';
          setTimeout(() => {
            event.target.style.transform = 'scale(1)';
          }, 200);
          
          return; // Don't proceed with normal node click handling
        }
      } else if (type === 'trigger' || isOptionNode) {
        // In keyword target selection mode, prevent clicking on trigger nodes and option nodes
        console.log('Trigger or option node clicked in keyword target selection mode - ignoring click');
        
        // Add visual feedback to indicate the click was ignored
        event.target.style.transform = 'scale(0.95)';
        event.target.style.transition = 'transform 0.1s';
        setTimeout(() => {
          event.target.style.transform = 'scale(1)';
        }, 100);
        
        return; // Don't proceed with normal node click handling
      }
    }
    
    // Check if we're in action selection mode
    if (window.handleActionSelection && typeof window.handleActionSelection === 'function') {
      console.log('Action selection mode detected, checking if this is an action node');
      
      // In action selection mode, only allow clicking on action nodes
      if (type === 'action' && stepRaw && stepRaw.action) {
        console.log('Action node clicked in selection mode');
        
        // Check if this action is from the same branch as the current selection context
        const currentBranchId = window.getCurrentSelectionBranchId && window.getCurrentSelectionBranchId();
        if (currentBranchId && branchId === currentBranchId) {
          console.log('Action from same branch clicked in selection mode - ignoring click');
          
          // Add visual feedback to indicate the click was ignored
          event.target.style.transform = 'scale(0.95)';
          event.target.style.transition = 'transform 0.1s';
          setTimeout(() => {
            event.target.style.transform = 'scale(1)';
          }, 100);
          
          return; // Don't proceed with normal node click handling
        }
        
        // Find the node index in the branch
        const branch = branches[branchId] || [];
        const nodeIndex = branch.findIndex(n => 
          n.id === nodeId || 
          n._id === nodeId || 
          n.id === parseInt(nodeId) || 
          n._id === parseInt(nodeId)
        );
        
        if (nodeIndex !== -1) {
          const actionData = {
            branchId,
            nodeIndex,
            action: stepRaw.action
          };
          
          console.log('Calling action selection handler with:', actionData);
          window.handleActionSelection(actionData);
          
          // Add visual feedback
          event.target.style.transform = 'scale(1.05)';
          event.target.style.transition = 'transform 0.2s';
          setTimeout(() => {
            event.target.style.transform = 'scale(1)';
          }, 200);
          
          return; // Don't proceed with normal node click handling
        }
      } else if (type === 'trigger' || isOptionNode) {
        // In action selection mode, prevent clicking on trigger nodes and option nodes
        console.log('Trigger or option node clicked in action selection mode - ignoring click');
        
        // Add visual feedback to indicate the click was ignored
        event.target.style.transform = 'scale(0.95)';
        event.target.style.transition = 'transform 0.1s';
        setTimeout(() => {
          event.target.style.transform = 'scale(1)';
        }, 100);
        
        return; // Don't proceed with normal node click handling
      }
    }
    
    // Option nodes are now triggers and can be edited
    if (isOptionNode) {
      console.log('Option node clicked - treating as trigger');
      // Continue to the trigger handling logic below
    }
    
    if (type === 'trigger') {
      console.log('Setting edit trigger with:', { ...stepRaw.trigger, branchId, nodeId });
      setEditTrigger({ ...stepRaw.trigger, branchId, nodeId });
      setOpenDrawer('trigger');
    } else if (type === 'action') {
      console.log('Setting edit action with:', { action: stepRaw.action, branchId, nodeId });
      setEditAction(stepRaw.action);
      setEditActionBranch(branchId);
      setEditActionNodeId(nodeId);
      setOpenDrawer('action');
    } else if (buttonText === 'Select') {
      console.log('Opening trigger drawer for default node');
      setOpenDrawer('trigger');
    } else if (buttonText === 'Set up') {
      console.log('Opening action drawer for default node');
      setOpenDrawer('action');
    }
  };

  // Determine if there are any nodes in the workflow
  const hasBranches = branches && Object.keys(branches).length > 0;
  const hasStartBranch = hasBranches && Array.isArray(branches.start) && branches.start.length > 0;
  


  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        position: "relative",
        flex: { xs: 1, sm: 1 },
        minHeight: { xs: 0, sm: 0 },
      }}
    >
      {(!hasBranches || !hasStartBranch) ? (
        <ReactFlow
          nodes={getNodes()}
          edges={getEdges()}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          style={{ background: isDark ? "#181818" : "#fff", height: "100%" }}
          onNodeClick={onNodeClick}
          minZoom={0.5}
          maxZoom={1.5}
        >
          <Background
            color={isDark ? "#222" : "#eee"}
            gap={16}
            variant="lines"
          />
          <Controls
            style={{
              background: isDark ? "#23272b" : "#fff",
              color: isDark ? "#fff" : "#222",
              border: `1px solid ${isDark ? "#444" : "#ccc"}`,
              boxShadow: isDark ? "0 2px 8px #0008" : "0 2px 8px #8882",
              borderRadius: 8,
              top: 16,
              right: 16,
              left: "auto",
              bottom: "auto",
              zIndex: 10,
            }}
            showInteractive={false}
          />
          <MiniMap
            nodeColor={(n) => n.data?.color || (isDark ? "#23272b" : "#B2F2D6")}
            maskColor={isDark ? "rgba(40,40,40,0.7)" : "rgba(200,200,200,0.6)"}
            style={{
              position: "absolute",
              right: 16,
              bottom: 16,
              background: isDark ? "#23272b" : "#fff",
              border: `1.5px solid ${isDark ? "#444" : "#ccc"}`,
              borderRadius: 8,
              boxShadow: isDark ? "0 2px 8px #0008" : "0 2px 8px #8882",
              zIndex: 11,
              width: 140,
              height: 100,
            }}
          />
        </ReactFlow>
      ) : (
        // Render dynamic nodes and edges for branches
        <ReactFlow
          nodes={nodesWithZIndex}
          edges={highlightedEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          style={{
            background: isDark ? "#181818" : "#fff",
            height: "100%",
            position: "relative",
            zIndex: 1
          }}
          onNodeClick={onNodeClick}
          onNodesChange={onNodesChange}
          onNodeMouseEnter={(event, node) => handleNodeHover(node.id)}
          onNodeMouseLeave={() => handleNodeHover(null)}
          minZoom={0.5}
          maxZoom={1.5}
        >
          <Background
            color={isDark ? "#222" : "#eee"}
            gap={16}
            variant="lines"
          />
          <Controls
            style={{
              background: isDark ? "#23272b" : "#fff",
              color: isDark ? "#fff" : "#222",
              border: `1px solid ${isDark ? "#444" : "#ccc"}`,
              boxShadow: isDark ? "0 2px 8px #0008" : "0 2px 8px #8882",
              borderRadius: 8,
              top: 16,
              right: 16,
              left: "auto",
              bottom: "auto",
              zIndex: 10,
            }}
            showInteractive={false}
          />
          <MiniMap
            nodeColor={(n) => n.data?.color || (isDark ? "#23272b" : "#B2F2D6")}
            maskColor={isDark ? "rgba(40,40,40,0.7)" : "rgba(200,200,200,0.6)"}
            style={{
              position: "absolute",
              right: 16,
              bottom: 16,
              background: isDark ? "#23272b" : "#fff",
              border: `1.5px solid ${isDark ? "#444" : "#ccc"}`,
              borderRadius: 8,
              boxShadow: isDark ? "0 2px 8px #0008" : "0 2px 8px #8882",
              zIndex: 11,
              width: 140,
              height: 100,
            }}
          />
        </ReactFlow>
      )}
      {openDrawer === "action" &&
        (selectedWorkflow?.channel === "facebook" ? (
          <ChooseFacebookActionDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditAction(null);
              setEditActionBranch(null);
              setEditActionNodeId(null);
              setPendingInsert(null);
            }}
            editAction={editAction}
            editActionBranch={editActionBranch}
            editActionNodeId={editActionNodeId}
            pendingInsert={pendingInsert}
          />
        ) : selectedWorkflow?.channel === "instagram" ? (
          <ChooseInstagramActionDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditAction(null);
              setEditActionBranch(null);
              setEditActionNodeId(null);
              setPendingInsert(null);
            }}
            editAction={editAction}
            editActionBranch={editActionBranch}
            editActionNodeId={editActionNodeId}
            pendingInsert={pendingInsert}
          />
        ) : (selectedWorkflow?.channel === "website" || selectedWorkflow?.channel === "whatsapp_web") ? (
          // A WhatsApp Web workflow answers in a browser chat, like the website chat: same set of steps.
          <ChooseWebsiteActionDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditAction(null);
              setEditActionBranch(null);
              setEditActionNodeId(null);
              setPendingInsert(null);
            }}
            editAction={editAction}
            editActionBranch={editActionBranch}
            editActionNodeId={editActionNodeId}
            pendingInsert={pendingInsert}
          />
        ) : (
          <ChooseActionDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditAction(null);
              setEditActionBranch(null);
              setEditActionNodeId(null);
              setPendingInsert(null);
            }}
            editAction={editAction}
            editActionBranch={editActionBranch}
            editActionNodeId={editActionNodeId}
            pendingInsert={pendingInsert}
          />
        ))}
      {openDrawer === "trigger" &&
        (selectedWorkflow?.channel === "facebook" ? (
          <ChooseFacebookTriggerDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditTrigger(null);
            }}
            editTrigger={editTrigger}
          />
        ) : selectedWorkflow?.channel === "instagram" ? (
          <ChooseInstagramTriggerDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditTrigger(null);
            }}
            editTrigger={editTrigger}
          />
        ) : selectedWorkflow?.channel === "website" ? (
          <ChooseWebsiteTriggerDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditTrigger(null);
            }}
            editTrigger={editTrigger}
          />
        ) : selectedWorkflow?.channel === "whatsapp_web" ? (
          <ChooseWhatsappWebTriggerDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditTrigger(null);
            }}
            editTrigger={editTrigger}
          />
        ) : (
          <ChooseTriggerDrawer
            open={true}
            onClose={() => {
              setOpenDrawer(null);
              setEditTrigger(null);
            }}
            editTrigger={editTrigger}
          />
        ))}
      
      <DeleteBranchModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setNodeToDelete(null);
        }}
        onConfirm={handleDeleteModalConfirm}
        branches={branches}
        nodeToDelete={nodeToDelete}
      />
    </Box>
  );
};

export default ActionsTab;


